import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { FormField } from '@/components/Form/FormField';
import { FormEvent, useEffect } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { EMAIL_REGEX, PHONE_REGEX } from '@/utils/regex.utils';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';

export const VehiculeCheckClientManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  /**
   * === Set Value Watchers section ===
   */
  const lastnameValue =
    watch &&
    watch(
      'individualLastname' as Path<TSchema>,
      data?.individualLastname ?? ''
    );

  const firstnameValue =
    watch &&
    watch(
      'individualFirstname' as Path<TSchema>,
      data?.individualFirstname ?? ''
    );

  const emailValue =
    watch &&
    watch('individualEmail' as Path<TSchema>, data?.individualEmail ?? '');

  const phoneValue =
    watch &&
    watch('individualPhone' as Path<TSchema>, data?.individualPhone ?? '');

  const addressLine1Value =
    watch &&
    watch(
      'individualAddressLine1' as Path<TSchema>,
      data?.individualAddressLine1 ?? ''
    );

  const postalCodeValue =
    watch &&
    watch(
      'individualPostalCode' as Path<TSchema>,
      data?.individualPostalCode ?? ''
    );

  const cityValue =
    watch &&
    watch('individualCity' as Path<TSchema>, data?.individualCity ?? '');

  const countryValue =
    watch &&
    watch('individualCountry' as Path<TSchema>, data?.individualCountry ?? '');
  /**
   * === END OF Set Value Watchers section ===
   */

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (lastnameValue) {
      resetFieldError<TSchema>({ fieldName: 'individualLastname', setError });
    }

    if (firstnameValue) {
      resetFieldError<TSchema>({ fieldName: 'individualFirstname', setError });
    }

    if (emailValue) {
      resetFieldError<TSchema>({ fieldName: 'individualEmail', setError });
    }

    if (phoneValue) {
      resetFieldError<TSchema>({ fieldName: 'individualPhone', setError });
    }

    if (addressLine1Value) {
      resetFieldError<TSchema>({
        fieldName: 'individualAddressLine1',
        setError,
      });
    }

    if (postalCodeValue) {
      resetFieldError<TSchema>({ fieldName: 'individualPostalCode', setError });
    }

    if (cityValue) {
      resetFieldError<TSchema>({ fieldName: 'individualCity', setError });
    }

    if (countryValue) {
      resetFieldError<TSchema>({ fieldName: 'individualCountry', setError });
    }
  }, [
    lastnameValue,
    firstnameValue,
    emailValue,
    phoneValue,
    addressLine1Value,
    postalCodeValue,
    cityValue,
    countryValue,
    setError,
  ]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue(
        'individualLastname' as Path<TSchema>,
        data.individualLastname ?? ''
      );

      setValue(
        'individualFirstname' as Path<TSchema>,
        data.individualFirstname ?? ''
      );

      setValue('individualEmail' as Path<TSchema>, data.individualEmail ?? '');

      setValue('individualPhone' as Path<TSchema>, data.individualPhone ?? '');

      setValue(
        'individualAddressLine1' as Path<TSchema>,
        data.individualAddressLine1 ?? ''
      );

      setValue(
        'individualAddressLine2' as Path<TSchema>,
        data.individualAddressLine2 ?? ''
      );

      setValue(
        'individualPostalCode' as Path<TSchema>,
        data.individualPostalCode ?? ''
      );

      setValue('individualCity' as Path<TSchema>, data.individualCity ?? '');

      setValue(
        'individualCountry' as Path<TSchema>,
        data.individualCountry ?? ''
      );
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!lastnameValue) {
      setError('individualLastname' as Path<TSchema>, {
        message: t('common:lastnameRequired'),
      });
    }

    if (!firstnameValue) {
      setError('individualFirstname' as Path<TSchema>, {
        message: t('common:firstnameRequired'),
      });
    }

    if (!emailValue) {
      setError('individualEmail' as Path<TSchema>, {
        message: t('common:emailRequired'),
      });
    } else if (!EMAIL_REGEX.test(emailValue)) {
      setError('individualEmail' as Path<TSchema>, {
        message: t('common:emailFormatInvalid'),
      });
    }

    if (!phoneValue) {
      setError('individualPhone' as Path<TSchema>, {
        message: t('common:phoneNumberRequired'),
      });
    } else if (!PHONE_REGEX.test(phoneValue)) {
      setError('individualPhone' as Path<TSchema>, {
        message: t('common:phoneNumberIncorrect'),
      });
    }

    if (!addressLine1Value) {
      setError('individualAddressLine1' as Path<TSchema>, {
        message: t('common:addressLine1Required'),
      });
    }

    if (!postalCodeValue) {
      setError('individualPostalCode' as Path<TSchema>, {
        message: t('common:postalCodeRequired'),
      });
    }

    if (!cityValue) {
      setError('individualCity' as Path<TSchema>, {
        message: t('common:cityRequired'),
      });
    }

    if (!countryValue) {
      setError('individualCountry' as Path<TSchema>, {
        message: t('common:countryRequired'),
      });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection
            text={t('common:client')}
            defaultIconClassName="ki-user"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={t('common:save')}
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormField
            text={t('common:lastName')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('individualLastname' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualLastname?.message as string)
                }
                placeholder={t('common:lastName')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:firstName')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('individualFirstname' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualFirstname?.message as string)
                }
                placeholder={t('common:firstName')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:email')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('individualEmail' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualEmail?.message as string)
                }
                placeholder={t('common:email')}
                type="email"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:phoneNumber')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('individualPhone' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualPhone?.message as string)
                }
                placeholder={t('common:phoneNumber')}
                type="tel"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:addressLine1')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('individualAddressLine1' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualAddressLine1?.message as string)
                }
                placeholder={t('common:addressLine1')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:addressLine2')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('individualAddressLine2' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualAddressLine2?.message as string)
                }
                placeholder={t('common:addressLine2')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:postalCode')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('individualPostalCode' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualPostalCode?.message as string)
                }
                placeholder={t('common:postalCode')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:city')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('individualCity' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualCity?.message as string)
                }
                placeholder={t('common:city')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:country')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('individualCountry' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.individualCountry?.message as string)
                }
                placeholder={t('common:country')}
                type="search"
              />
            </div>
          </FormField>
        </>
      </Card>
    </form>
  );
};
